import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import MobileHeader from "../../common/components/mobile-header-component/MobileHeader";
import PriceDetails from "./components/PriceDetails";
import CartItemCard from "./components/CartItemCard";
import {
  getCartSummary,
  getCartCount,
  deletePartnerByCartItemId,
  deletePartnerByServiceId,
} from "../../api/cart.api";
import "./cart-page.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import ToastMessage from "../../common/components/toast-message/toast-message";

type PlannerPackageItem = {
  cartItemId: number;
  planner: {
    plannerId: number;
    plannerName: string;
    plannerLocation?: string;
    plannerAvgRating?: number | null;
    plannerTotalReviews?: number | null;
    plannerImages?: any[];
  };
  package: {
    id: number;
    name: string;
    description?: string;
    services?: { serviceName: string | null }[];
    categories?: { id: number; name: string }[];
    price?: number;
    discount?: number;
    finalPrice?: number;
  };
};

export default function CartPage() {
  const [cartSummary, setCartSummary] = useState<any>(null);
  const [plannerPackages, setPlannerPackages] = useState<PlannerPackageItem[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Provider selection
  const [selectedPartners, setSelectedPartners] = useState<number[]>([]);

  // Planner selection
  const [selectedPlannerCartItemIds, setSelectedPlannerCartItemIds] = useState<
    number[]
  >([]);

  /**
   *  Single-source selection lock:
   * - Only ONE checkbox across provider+planner can be selected at a time.
   * - Format: "provider:<partnerId>" OR "planner:<cartItemId>"
   */
  const [selectedItemKey, setSelectedItemKey] = useState<string | null>(null);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });

  const location = useLocation();

  // Detect navigation source (breadcrumbs only)
  const isFromPlans = location.state?.isFromPlans ?? false;
  const partnerId = location.state?.partnerId;
  const isFromDetailsPg = location.state?.isFromDetails ?? false;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // show breadcrumbs ONLY when coming from plans or details page
  const showBreadcrumbs = isFromPlans || isFromDetailsPg;

  const breadcrumbItems = useMemo(() => {
    if (isFromPlans) {
      const plannerIdFromState = location.state?.plannerId;

      return [
        {
          label: "Detail page",
          to: plannerIdFromState
            ? `/home/list-of-funeral-homes/preneed-planners/${plannerIdFromState}`
            : undefined,
        },
        { label: "Selected Package" },
      ];
    }

    return isFromDetailsPg
      ? [
          {
            label: "Detail page",
            to: `/home/list-of-funeral-homes/details/${partnerId}`,
          },
          { label: "Selected Services" },
        ]
      : [{ label: "Selected Services" }];
  }, [isFromPlans, isFromDetailsPg, partnerId, location.state]);

  /* =========================================================
     GET CART SUMMARY (single API for both services + packages)
     ========================================================= */

  const fetchCartSummary = async () => {
    try {
      setLoading(true);

      const response = await getCartSummary();
      const apiData = response?.data?.data?.data;

      if (!apiData) {
        setError("Failed to load cart summary");
        setCartSummary(null);
        setPlannerPackages([]);
        return;
      }

      // partnerItems can be null
      const partners = apiData?.partnerItems?.partners ?? [];
      const partnerItemsSummary = apiData?.partnerItems?.summary ?? null;

      const packages = apiData?.plannerPackages ?? [];
      setPlannerPackages(Array.isArray(packages) ? packages : []);

      // always set cartSummary even if partners empty
      setCartSummary({
        cartId: apiData?.cartId,
        partners: Array.isArray(partners) ? partners : [],
        summary: partnerItemsSummary ?? apiData?.summary ?? {},
        grandTotal:
          partnerItemsSummary?.grandTotal ?? apiData?.summary?.grandTotal ?? 0,
      });

      setError(null);
    } catch (error: any) {
      console.error("CART SUMMARY ERROR::", error);

      if (
        error?.response?.status === 404 ||
        error?.response?.data?.errorCode === "CART_NOT_FOUND"
      ) {
        setCartSummary({ partners: [], grandTotal: 0, summary: {} });
        setPlannerPackages([]);
        setSelectedPartners([]);
        setSelectedPlannerCartItemIds([]);
        setSelectedItemKey(null);
        setError(null);
      } else {
        setError("Something went wrong while fetching cart summary");
        setCartSummary(null);
        setPlannerPackages([]);
        setSelectedPartners([]);
        setSelectedPlannerCartItemIds([]);
        setSelectedItemKey(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCartCount = async () => {
    try {
      const res = await getCartCount();
      const cartCount = res.data?.data?.data?.totalCount ?? 0;

      dispatch({
        type: "CART:SET_COUNT",
        payload: { count: cartCount },
      });
    } catch (error) {
      console.error("Failed to fetch cart count:", error);
    }
  };

  //  NOTE: Planner delete uses same delete-by-cartItemId API
  const handleDeleteService = async (serviceId: number) => {
    try {
      await deletePartnerByServiceId(serviceId);
      await fetchCartSummary();
      await fetchCartCount();
    } catch (error) {
      console.error("ERROR DELETING SERVICE::", error);
    }
  };

  const handleDeleteSubService = async (cartItemId: number) => {
    try {
      await deletePartnerByCartItemId(cartItemId);
      await fetchCartSummary();
      await fetchCartCount();
    } catch (error) {
      console.error("ERROR DELETING SUB-SERVICE::", error);
    }
  };

  //  NEW: delete planner package by cartItemId
  const handleDeletePlannerPackage = async (plannerCartItemId: number) => {
    try {
      await deletePartnerByCartItemId(plannerCartItemId);
      await fetchCartSummary();
      await fetchCartCount();

      // if deleted item was selected, clear selection
      if (selectedItemKey === `planner:${plannerCartItemId}`) {
        setSelectedItemKey(null);
        setSelectedPlannerCartItemIds([]);
      }
    } catch (error) {
      console.error("ERROR DELETING PLANNER PACKAGE::", error);
    }
  };

  const handlePartnerSelect = (pId: number) => {
    const key = `provider:${pId}`;

    setSelectedItemKey((prevKey) => {
      const isTogglingOff = prevKey === key;

      // Toggle off
      if (isTogglingOff) {
        setSelectedPartners([]);
        return null;
      }

      // Toggle on (single select)
      setSelectedPartners([pId]);
      setSelectedPlannerCartItemIds([]);
      return key;
    });
  };

  const handlePlannerSelect = (cartItemId: number) => {
    const key = `planner:${cartItemId}`;

    setSelectedItemKey((prevKey) => {
      const isTogglingOff = prevKey === key;

      if (isTogglingOff) {
        setSelectedPlannerCartItemIds([]);
        return null;
      }

      setSelectedPlannerCartItemIds([cartItemId]);
      setSelectedPartners([]);
      return key;
    });
  };

  useEffect(() => {
    fetchCartSummary();
    fetchCartCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  /* =========================================================
     Adapt plannerPackages -> CartItemCard format
     (planner UI flag + price + delete support)
     ========================================================= */

  const plannerCards = useMemo(() => {
    if (!Array.isArray(plannerPackages)) return [];

    return plannerPackages.map((p) => {
      const planner = p.planner ?? ({} as any);
      const pkg = p.package ?? ({} as any);

      const plannerImages = Array.isArray(planner?.plannerImages)
        ? planner.plannerImages
        : [];

      const pkgPrice = pkg.finalPrice ?? pkg.price ?? 0;

      return {
        //  IMPORTANT: use cartItemId as unique id for selection
        partnerId: p.cartItemId,
        plannerId: planner.plannerId,

        partnerName: planner.plannerName ?? "-",
        partnerLocation: planner.plannerLocation ?? "",
        partnerAvgRating: planner.plannerAvgRating ?? 0,
        partnerTotalReviews: planner.plannerTotalReviews ?? 0,
        partnerImages: plannerImages,

        //  Planner-specific flag (CartItemCard should use to render planner layout)
        isPlannerPackage: true,

        // Data for planner card layout (screenshot-like)
        packageId: pkg.id,
        packageName: pkg.name,
        packageDescription: pkg.description ?? "",
        packageServices: (pkg.categories ?? pkg.services ?? [])
          .map((c: any) => ({
            serviceName: c.name ?? c.serviceName,
            needType: c.needType,
          }))
          .filter((s: any) => !!s?.serviceName),
        packagePrice: pkgPrice,
        needType: pkg.needType,
        // PriceDetails expects partnerTotalPrice
        partnerTotalPrice: pkgPrice,

        // Used for delete in CartItemCard
        cartItemId: p.cartItemId,

        _rawPlannerPackage: p,
      };
    });
  }, [plannerPackages]);

  //  Derived values BEFORE early returns (fixes hook order issues)
  const providerCards = cartSummary?.partners ?? [];
  const totalItemsCount =
    (providerCards?.length ?? 0) + (plannerCards?.length ?? 0);
  const showAnyCheckbox = totalItemsCount > 1;

  //  Disable other checkboxes when one is selected
  const plannerListDisabled = selectedItemKey?.startsWith("provider:") ?? false;
  const providerListDisabled = selectedItemKey?.startsWith("planner:") ?? false;

  //  No useMemo here (prevents hook-order bug)
  const selectedPartnersForPriceDetails: number[] = (() => {
    if (!selectedItemKey) return [];
    const parts = selectedItemKey.split(":");
    const id = Number(parts[1]);
    return Number.isFinite(id) ? [id] : [];
  })();

  const isCartEmpty = totalItemsCount === 0;

  /* =========================================================
     LOADING / ERROR
     ========================================================= */

  if (loading) {
    return (
      <Box className="cart-page__loading">
        <CircularProgress
          size="3rem"
          className="cart-page__circular-progress"
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        sx={{ textAlign: "center", mt: 4, color: "red" }}
        className="cart-page__error-text"
      >
        {error}
      </Typography>
    );
  }

  /* =========================================================
     EMPTY STATE only when BOTH are empty
     ========================================================= */

  if (isCartEmpty) {
    return (
      <Container className="cart-page">
        <Typography className="cart-page__heading">Selected Items</Typography>

        <Box className="cart-page__empty-page">
          <Box className="cart-page__empty">
            <Box className="cart-page__empty-inner">
              <Typography className="cart-page__empty-title">
                Your cart is empty
              </Typography>
              <Typography className="cart-page__empty-subtitle">
                Looks like you have not made any choice.
              </Typography>
              <Box className="cart-page__empty-btn-wrapper">
                <Button
                  className="cart-page__empty-btn"
                  onClick={() => navigate("/home")}
                >
                  Back to home
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  /* =========================================================
      SINGLE SOURCE OF TRUTH UI:
     Always show ALL items: planner + provider together
     ========================================================= */

  return (
    <>
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />

      {isMobile && (
        <MobileHeader
          title="Selected Items"
          onBack={() => window.history.back()}
          className="cart-page__mobile-header"
          style={{ backgroundColor: "#f8f9fa" }}
        />
      )}

      <Container className="cart-page">
        {showBreadcrumbs ? <CustomBreadcrumbs items={breadcrumbItems} /> : null}

        <Typography className="cart-page__heading-filled">
          Selected Items
        </Typography>

        <Box className="cart-page__content">
          {/* LEFT: Orders */}
          <Box className="cart-page__shopping">
            <Box className="cart-page__shopping-header">
              Orders{" "}
              <span className="cart-page__shopping-header--light">
                ({totalItemsCount})
              </span>
            </Box>

            <Box className="cart-page__shopping-list">
              {/* ===== Provider items (services) ===== */}
              {providerCards.map((partner: any) => {
                const key = `provider:${partner.partnerId}`;
                const isDisabled =
                  providerListDisabled && selectedItemKey !== key;

                return (
                  <Box
                    key={partner.partnerId}
                    sx={{
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <CartItemCard
                      item={partner}
                      onDeleteService={handleDeleteService}
                      onDeleteSubService={handleDeleteSubService}
                      isSelected={selectedPartners.includes(partner.partnerId)}
                      onSelect={() => handlePartnerSelect(partner.partnerId)}
                      showCheckbox={showAnyCheckbox}
                      isDisabled={isDisabled}
                    />
                  </Box>
                );
              })}

              {/* ===== Planner items (packages) ===== */}
              {plannerCards.map((pkgItem: any) => {
                const key = `planner:${pkgItem.partnerId}`;
                const isDisabled =
                  plannerListDisabled && selectedItemKey !== key;

                return (
                  <Box
                    key={pkgItem.partnerId}
                    sx={{
                      opacity: isDisabled ? 0.5 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                    }}
                  >
                    <CartItemCard
                      item={pkgItem}
                      //  Delete planner package using cartItemId
                      onDeleteService={(cartItemId: number) =>
                        handleDeletePlannerPackage(cartItemId)
                      }
                      // not used for planner
                      onDeleteSubService={() => {}}
                      isSelected={selectedPlannerCartItemIds.includes(
                        pkgItem.partnerId,
                      )}
                      onSelect={() => handlePlannerSelect(pkgItem.partnerId)}
                      showCheckbox={showAnyCheckbox}
                      isDisabled={isDisabled}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* RIGHT: Price Details */}
          <Box className="cart-page__summary">
            <Typography className="cart-page__summary-title">
              Price Details
            </Typography>

            {/*  Key behavior:
                - if user selects provider OR planner, pass ONLY that selection to PriceDetails
                - else pass empty => PriceDetails should show all (existing behavior)
             */}
            <PriceDetails
              cartSummary={{
                //  combine everything for PriceDetails list (single source)
                partners: [...providerCards, ...plannerCards],
                cartId: cartSummary?.cartId,
                summary: cartSummary?.summary ?? {},
                grandTotal: cartSummary?.grandTotal ?? 0,
              }}
              partnerId={partnerId}
              isFromDetailsPg={isFromDetailsPg}
              selectedPartners={selectedPartnersForPriceDetails}
              setToast={setToast}
            />
          </Box>
        </Box>
      </Container>
    </>
  );
}
